import React, {
    useEffect,
    useState,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Col, Row, Spin, Empty, Badge, Typography } from 'antd';
import ContainerAggregation from './components/ContainerAddForm';
import ContainerGauges from './components/ContainerGauges';

import {
    getPagePermissions,
    DISPENSARY_PERMISSION,
} from '../../security/permissions';
import DistributionPointSelected from '../../context/MyDistributionPoint';

import './styles.css';
import { useClient } from 'hooks/useClient';
import { ContainerResource, getContainers } from 'api/generated';
import { handleAxiosError } from 'common/helpers/generalUtils';
import useLoggedUser from 'hooks/useLoggedUser';

const { Title, Text } = Typography;

function Containers() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [containers, setContainers] = useState<ContainerResource[]>([]);
    const [loadingContainers, setLoadingContainers] = useState(false);

    const { client } = useClient();
    const { permissions } = useLoggedUser();
    const { distributionPoint } = useContext(DistributionPointSelected);

    const dispensaryPermissions = getPagePermissions(DISPENSARY_PERMISSION, permissions);
    const areThereContainers = containers.length > 0;

    const fetchContainers = useCallback(() => {
        setLoadingContainers(true);
        getContainers({
            client,
            path: { distributionPointId: distributionPoint?.id ?? '' },
        })
            .then(({ data }) => setContainers(data ?? []))
            .catch((error) => handleAxiosError(error, 'No se pudieron obtener contenedores'))
            .finally(() => setLoadingContainers(false));
    }, [distributionPoint, client]);

    useEffect(() => {
        fetchContainers();
    }, [fetchContainers]);

    const handleAddContainerSuccess = () => {
        setDrawerOpen(false);
        fetchContainers();
    };

    const activeCount = useMemo(
        () => containers.filter((c) => c.containerStatus).length,
        [containers]
    );

    const cardItems = useMemo(
        () =>
            containers.map((container) => (
                <Col
                    key={container.containerKey}
                    xs={24}
                    sm={24}
                    md={12}
                    lg={12}
                    xl={8}
                    style={{ marginBottom: 24 }}
                >
                    <ContainerGauges
                        key={container.idContainer}
                        container={container}
                        onUpdateContainers={fetchContainers}
                    />
                </Col>
            )),
        [containers, fetchContainers]
    );

    return (
        <Spin size="large" tip="Cargando contenedores..." spinning={loadingContainers}>
            <div className="containers-wrapper">
                <div className="containers-header">
                    <div className="containers-header__info">
                        <Title level={3} className="containers-header__title">
                            Contenedores
                        </Title>
                        {areThereContainers && (
                            <div className="containers-header__badges">
                                <Badge
                                    count={containers.length}
                                    style={{ backgroundColor: '#3b82f6' }}
                                    overflowCount={99}
                                />
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    Total
                                </Text>
                                <Badge
                                    count={activeCount}
                                    style={{ backgroundColor: '#10b981', marginLeft: 16 }}
                                    overflowCount={99}
                                />
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    Activos
                                </Text>
                            </div>
                        )}
                    </div>

                    <div className="containers-header__actions">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchContainers}
                            className="containers-btn--refresh"
                        >
                            Actualizar
                        </Button>
                        {dispensaryPermissions.add && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setDrawerOpen(true)}
                                className="containers-btn--add"
                            >
                                Agregar contenedor
                            </Button>
                        )}
                    </div>
                </div>

                {areThereContainers ? (
                    <Row gutter={[24, 24]} className="containers-grid">
                        {cardItems}
                    </Row>
                ) : (
                    <div className="containers-empty">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span className="containers-empty__text">
                                    Sin contenedores agregados
                                </span>
                            }
                        >
                            {dispensaryPermissions.add && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setDrawerOpen(true)}
                                    className="containers-btn--add"
                                >
                                    Agregar primer contenedor
                                </Button>
                            )}
                        </Empty>
                    </div>
                )}
            </div>

            <ContainerAggregation
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSuccess={handleAddContainerSuccess}
            />
        </Spin>
    );
}

export default Containers;